import { DisputeAlertDecision } from "@redotech/cockroachdb-codegen/tables/dispute-alert-decision";
import { minorUnitsToAmount } from "@redotech/money/currencies";
import * as z from "zod/v4";

/**
 * Pre-dispute alert storage. Mirrors the `dispute` table. Disputely is the only
 * provider today, so there is no `source` enum column; the vendor-specific wire
 * parsing/mapping lives in the Disputely client package. Field set +
 * required/optional mirror the live Disputely §3.4 field reference.
 */

/** Card-network alert program. */
export const disputeAlertType = ["rdr", "cdrn", "ethoca"] as const;

export type DisputeAlertType = (typeof disputeAlertType)[number];

export const disputeAlertStatus = [
  "needs_review",
  "resolved",
  "cancelled",
] as const;

export type DisputeAlertStatus = (typeof disputeAlertStatus)[number];

/**
 * Terminal statuses: once an alert is resolved or cancelled, an out-of-order
 * re-delivery must not regress it back to `needs_review`. Used by the repo's
 * upsert guard.
 */
export const terminalDisputeAlertStatuses = ["resolved", "cancelled"] as const;

/**
 * Columns the alert list views (merchant list + admin tab) can sort by. Both are
 * backed by read-path indexes — `createdAt` by the account_created / created
 * indexes, `respondBy` by the account_respond_by / respond_by indexes. Mirrors
 * `disputeSortableColumns` on the dispute table.
 */
export const disputeAlertSortableColumns = ["createdAt", "respondBy"] as const;

export type DisputeAlertSortableColumn =
  (typeof disputeAlertSortableColumns)[number];

/**
 * Which automation rule produced the (auto-refund) decision. `default_resolve`
 * is the no-rules-configured default posture (refund every matched alert);
 * `filter_match` means a merchant-configured filter group matched;
 * `already_refunded` means the order was already fully refunded (resolved with
 * no new refund). Automation only ever refunds — there is no auto-decline.
 */
export const disputeAlertDecisionRule = [
  "default_resolve",
  "already_refunded",
  "filter_match",
] as const;

export type DisputeAlertDecisionRule =
  (typeof disputeAlertDecisionRule)[number];

/**
 * Why automation left an alert for manual review. (A network with no configured
 * rules defaults to resolving every alert, so "no rules" is never a
 * manual-review reason — see `default_resolve`.)
 */
export const disputeAlertAutomationError = [
  "no_rule_matched",
  "no_order_match",
  "multiple_matches",
  "order_currency_mismatch",
  "window_passed",
  // The matched transaction's live refundable state couldn't be determined from
  // Shopify (e.g. an unmappable authorization→capture chain), so we can't size a
  // refund — hand to a human rather than guess.
  "transaction_not_refundable",
  "refund_failed",
  "provider_rejected",
  // Disputely returned 2xx but not the RESOLVED status we expected for the
  // action — a provider-contract anomaly; hand to a human to reconcile.
  "unexpected_provider_status",
  // The refund succeeded but every resolve attempt failed with a retryable
  // provider/network error and the retry budget was exhausted — the customer is
  // refunded but Disputely was never told; hand to a human to reconcile.
  "provider_unavailable",
] as const;

export type DisputeAlertAutomationError =
  (typeof disputeAlertAutomationError)[number];

const disputeAlertSchema_ = z.object({
  // Our row's primary key (CRDB random_id), not Disputely's.
  id: z.coerce.bigint(),
  // OUR CRDB account.id (FK to `account`), resolved from the Disputely account
  // id at sync time. Not the Disputely account id.
  accountId: z.coerce.bigint(),
  // Disputely's ALERT id (the wire payload's `id`); the per-account dedupe key.
  sourceAlertId: z.string(),
  type: z.enum(disputeAlertType),
  providerAlertType: z.string().nullish(),
  status: z.enum(disputeAlertStatus),
  reasonCode: z.string().nullish(),
  alertTimestamp: z.coerce.date().nullish(),
  respondBy: z.coerce.date().nullish(),
  networkTimestamp: z.coerce.date().nullish(),
  caseDate: z.coerce.date().nullish(),
  // Amounts are MAJOR units here (e.g. 49.99), even though the DB columns are
  // integer minor units (`*_amount_minor` INT8). The repo converts at its own
  // boundary (amountToMinorUnits on write, disputeAlertRowToDomain on read), so
  // consumers (decision engine, Disputely API, Shopify refund, UI) stay major.
  disputeAmount: z.coerce.number(),
  disputeCurrency: z.string(),
  referenceTransactionAmount: z.coerce.number(),
  referenceTransactionCurrency: z.string(),
  referenceTransactionDate: z.coerce.date(),
  authCode: z.string().nullish(),
  acquirerReferenceNumber: z.string(),
  cardBin: z.string().nullish(),
  cardLastFour: z.string().nullish(),
  cardAccountNumber: z.string().nullish(),
  paymentType: z.string().nullish(),
  paymentDescriptor: z.string().nullish(),
  paymentDescriptorContact: z.string().nullish(),
  networkDescriptor: z.string().nullish(),
  mcc: z.string().nullish(),
  source: z.string().nullish(),
  issuer: z.string().nullish(),
  liability: z.string().nullish(),
  merchantName: z.string().nullish(),
  transactionId: z.string().nullish(),
  age: z.coerce.number().nullish(),
  respondedAt: z.coerce.date().nullish(),
  respondedBy: z.string().nullish(),
  refundedAmount: z.coerce.number().nullish(),
  refundedCurrency: z.string().nullish(),
  declineReason: z.string().nullish(),
  rdrMatchFound: z.boolean().nullish(),
  rdrProcessed: z.boolean().nullish(),
  rdrProcessedAt: z.coerce.date().nullish(),
  rdrProcessorUsed: z.string().nullish(),
  decision: z.enum(DisputeAlertDecision).nullish(),
  decisionRule: z.string().nullish(),
  decisionAt: z.coerce.date().nullish(),
  platformRefundId: z.string().nullish(),
  // The Redo order (Mongo `_id`) we refunded, recorded at refund time so the
  // detail page can resolve it via an indexed Order.findById fallback.
  refundedOrderId: z.string().nullish(),
  automationError: z.string().nullish(),
  rawAlert: z.unknown(),
  createdAt: z.coerce.date(),
  updatedAt: z.coerce.date(),
  deletedAt: z.coerce.date().nullish(),
});
export const disputeAlertSchema: z.ZodType<DisputeAlert> = disputeAlertSchema_;
export interface DisputeAlert extends z.infer<typeof disputeAlertSchema_> {}

export { disputeAlertSchema_ };

/**
 * The raw `dispute_alert` DB row as the repo SELECTs it: identical to
 * {@link DisputeAlert} except the three amounts are integer minor units
 * (`*_amount_minor` columns), matching `order_transaction_details.amount_minor`
 * and `dispute_deflection.*_amount_minor`. Disputely's wire amounts arrive in
 * major units; the repo converts to minor on write and back to major (via
 * {@link disputeAlertRowToDomain}) on read, so the public {@link DisputeAlert}
 * stays in major units and nothing above the repo has to change.
 */
const disputeAlertRowSchema_ = disputeAlertSchema_
  .omit({
    disputeAmount: true,
    referenceTransactionAmount: true,
    refundedAmount: true,
  })
  .extend({
    disputeAmountMinor: z.coerce.number().int(),
    referenceTransactionAmountMinor: z.coerce.number().int(),
    refundedAmountMinor: z.coerce.number().int().nullish(),
  });
export type DisputeAlertRow = z.infer<typeof disputeAlertRowSchema_>;
export const disputeAlertRowSchema: z.ZodType<DisputeAlertRow> =
  disputeAlertRowSchema_;

/**
 * Scales a raw minor-unit row up to the major-unit domain {@link DisputeAlert}.
 * Each amount is divided by its own paired currency's minor-unit scale; the
 * refund amount falls back to the dispute currency when the row carries no
 * `refunded_currency`.
 */
export function disputeAlertRowToDomain(row: DisputeAlertRow): DisputeAlert {
  const {
    disputeAmountMinor,
    referenceTransactionAmountMinor,
    refundedAmountMinor,
    ...rest
  } = row;
  return {
    ...rest,
    disputeAmount: minorUnitsToAmount(disputeAmountMinor, row.disputeCurrency),
    referenceTransactionAmount: minorUnitsToAmount(
      referenceTransactionAmountMinor,
      row.referenceTransactionCurrency,
    ),
    refundedAmount:
      refundedAmountMinor == null
        ? refundedAmountMinor
        : minorUnitsToAmount(
            refundedAmountMinor,
            row.refundedCurrency ?? row.disputeCurrency,
          ),
  };
}

export interface InsertDisputeAlertInput {
  /** OUR CRDB account.id (resolved from the Disputely account id), not Disputely's. */
  accountId: bigint;
  /** Disputely's alert id (the wire `id`). */
  sourceAlertId: string;
  type: DisputeAlertType;
  providerAlertType?: string | null;
  status: DisputeAlertStatus;
  reasonCode?: string | null;
  alertTimestamp?: Date | null;
  respondBy?: Date | null;
  networkTimestamp?: Date | null;
  caseDate?: Date | null;
  disputeAmount: number;
  disputeCurrency: string;
  referenceTransactionAmount: number;
  referenceTransactionCurrency: string;
  referenceTransactionDate: Date;
  authCode?: string | null;
  acquirerReferenceNumber: string;
  cardBin?: string | null;
  cardLastFour?: string | null;
  cardAccountNumber?: string | null;
  paymentType?: string | null;
  paymentDescriptor?: string | null;
  paymentDescriptorContact?: string | null;
  networkDescriptor?: string | null;
  mcc?: string | null;
  source?: string | null;
  issuer?: string | null;
  liability?: string | null;
  merchantName?: string | null;
  transactionId?: string | null;
  age?: number | null;
  respondedAt?: Date | null;
  respondedBy?: string | null;
  refundedAmount?: number | null;
  refundedCurrency?: string | null;
  declineReason?: string | null;
  rdrMatchFound?: boolean | null;
  rdrProcessed?: boolean | null;
  rdrProcessedAt?: Date | null;
  rdrProcessorUsed?: string | null;
  rawAlert: unknown;
}

/**
 * Outcome of Redo's automation pass over an actionable alert. `manual_review`
 * means automation evaluated the alert and deliberately left it for a human
 * (see `automationError` for why); a null `decision` means automation never
 * ran (RDR, arrived-terminal, or pre-automation rows). Values come from the
 * generated `dispute_alert_decision` CockroachDB enum (see docs/db-enums.md);
 * never hand-mirror them here.
 */
// eslint-disable-next-line @redotech/no-re-exports -- TODO: remove re-export, update all import sites
export { DisputeAlertDecision } from "@redotech/cockroachdb-codegen/tables/dispute-alert-decision";
